//#region IMPORTS
import {
  clearInstoreOrders,
  instoreOrdersSelectors,
  fetchInstoreOrdersAsync,
  updateInstoreOrderAsync,
  deleteInstoreOrderAsync,
} from "../../features/instoreOrders/instoreOrdersSlice";
import {
  CANCEL_INSTORE_ORDER_SURE,
  DELETE_INSTORE_ORDER_SURE,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { toBackendDate } from "../../helpers/toBackendDate";
import { InstoreOrder } from "../../models/instoreOrder";
import { ORDERSTATUS } from "../../models/order";
import { useHistory } from "react-router-dom";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import InstoreOrdersSettingsDropdown from "./InstoreOrdersSettingsDropdown";
import InstoreOrdersFilterBar from "./InstoreOrdersFilterBar";
import InstoreOrdersTable from "./InstoreOrdersTable";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../genericPageHeader/PageHeader";
import AreYouSure from "../modalTypes/AreYouSure";
import MainModal from "../_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function InstoreOrdersContainer({
  title = "rituals.instoreOrders",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const instoreOrders = useAppSelector(instoreOrdersSelectors.selectAll);
  const { loading, instoreOrdersLoaded, metaData } = useAppSelector(
    ({ instoreOrders }) => instoreOrders
  );
  const { user } = useAppSelector((state) => state.account);

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  // Local state
  const [instoreOrderToManage, setInstoreOrderToManage] =
    useState<InstoreOrder | null>(null);
  //#endregion

  //#region LOGIC
  // Reset handler
  const resetHandler = async () => {
    await dispatch(fetchInstoreOrdersAsync());
  };

  // Create new instore order
  const createNewInstoreOrder = () => {
    dispatch(clearInstoreOrders());
    history.push("/instoreorders/create", true);
  };

  // Change orderstatus shortcut handler
  const changeOrderStatusHandler = async (
    instoreOrder: InstoreOrder,
    order_status_name: string
  ) => {
    try {
      await dispatch(
        updateInstoreOrderAsync({
          id: instoreOrder.id,
          values: {
            order_status: order_status_name,
            order_code: instoreOrder.order_code,
            order_type: instoreOrder.order_type,
            order_date: toBackendDate(instoreOrder.order_date),
            instore_type_id: instoreOrder.instore_type_id,
            template_id: instoreOrder.template_id,
          },
        })
      );
    } catch (error) {}
    cancelChangeInstoreOrder();
  };

  // Check for warning when changing orderstatus to cancelled
  const checkIfWarningIsNeeded = async (
    instoreOrder: InstoreOrder,
    order_status_name: string
  ) => {
    if (order_status_name === ORDERSTATUS.cancelled) {
      setInstoreOrderToManage(instoreOrder);
      dispatch(openModal(CANCEL_INSTORE_ORDER_SURE));
    } else {
      changeOrderStatusHandler(instoreOrder, order_status_name);
    }
  };

  // Cancel changing an instore order
  const cancelChangeInstoreOrder = () => {
    setInstoreOrderToManage(null);
    dispatch(openModal(false));
  };
  // Delete template warning
  const deleteInstoreOrderWarningHandler = (instoreOrder: InstoreOrder) => {
    setInstoreOrderToManage(instoreOrder);
    dispatch(openModal(DELETE_INSTORE_ORDER_SURE));
  };

  // Delete template confirm
  const deleteInstoreOrderHandler = async () => {
    if (instoreOrderToManage) {
      await dispatch(deleteInstoreOrderAsync({ id: instoreOrderToManage.id }));
      dispatch(openModal(false));
    }
  };
  //#endregion

  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={CANCEL_INSTORE_ORDER_SURE}
        closeHandler={cancelChangeInstoreOrder}
      >
        <AreYouSure
          closeHandler={cancelChangeInstoreOrder}
          confirmHandler={() =>
            instoreOrderToManage &&
            changeOrderStatusHandler(instoreOrderToManage, "Cancelled")
          }
          title="Cancel order?"
          message={`Are you sure you want to cancel order ${instoreOrderToManage?.order_code}`}
        />
      </MainModal>

      <MainModal
        type={DELETE_INSTORE_ORDER_SURE}
        closeHandler={cancelChangeInstoreOrder}
      >
        <AreYouSure
          confirmHandler={deleteInstoreOrderHandler}
          closeHandler={cancelChangeInstoreOrder}
          message={`Are you sure you want to delete instore order ${instoreOrderToManage?.order_code}?`}
          title="Delete instore order?"
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          createButtonValue={t("rituals.createNewOrder")}
          createClickHandler={createNewInstoreOrder}
          extraHQButtons={<InstoreOrdersSettingsDropdown />}
          disabled={loading}
          isAdmin={isAdmin}
          hqCanAdd
          hqTeam={hqTeam}
          title={t(title)}
          reset={resetHandler}
        />

        <InstoreOrdersFilterBar instoreOrders={instoreOrders} />

        <InstoreOrdersTable
          changeOrderStatusHandler={(
            instoreOrder: InstoreOrder,
            status: string
          ) => checkIfWarningIsNeeded(instoreOrder, status)}
          deleteHandler={(item: InstoreOrder) =>
            deleteInstoreOrderWarningHandler(item)
          }
          itemsLoaded={instoreOrdersLoaded}
          metaData={metaData}
          items={instoreOrders}
          hide={instoreOrdersLoaded && instoreOrders.length <= 0}
        />
      </MainContainer>
    </Fragment>
  );
}
